import { OrderQuery } from '../../../../../../services/query';
import { useEffect, useState, useMemo } from 'react';
import { OrderCommand, PaymentCommand } from '../../../../../../services/command';
import { useSelector } from 'react-redux';
import { SCUIButton } from '../../../../../../atomic/atoms';
import {
    Button,
    Card,
    CardContent,
    Divider,
    FormControl,
    MenuItem,
    Select,
    TextField,
    Typography,
} from '@mui/material';
import Swal from 'sweetalert2';
import CloseIcon from '@mui/icons-material/Close';
import PackageDataDispatcher from './components/PackageDataDispatcher';
import OtpOrIdentityVerification from './components/OtpOrIdentityVerification';
import { useTranslation } from 'react-i18next';
import { UOSSU } from './userOrderShipmentStatusUpdate.classes';
import { getShipmentVerificationType, validateShipmentStatusSave } from './UserOrderShipmentStatusUpdate.helper';

const TR_COUNTRY_ID = 1;
const COD_PAYMENT_METHOD_ID = 7;
const ShippingListDeliveredStatus = 6;

function UserOrderShipmentStatusUpdate({ shippingListId, shipment, handleCloseModal }: any) {
    const { t } = useTranslation();
    const [selectedState, setSelectedState] = useState('');
    const [selectedCancelState, setSelectedCancelState] = useState('');
    const [shipmentStatusState, setShipmentStatusState] = useState<any>(null);
    const [shipmentStatuses, setShipmentStatuses] = useState([]);
    const [cancelReasons, setCancelReasons] = useState<any>(null);
    const [isLoading, setIsLoading] = useState(false);
    const [isError, setIsError] = useState(false);
    const [textBoxValue, setTextBoxValue] = useState('');
    const [textBoxDesi, setTextBoxDesi] = useState<any>();
    const [textBoxPackageCount, setTextBoxPackageCount] = useState<any>();
    const [otpVerificationCode, setOtpVerificationCode] = useState<any>();
    const [isVerified, setIsVerified] = useState(false);
    const [selectedOption, setSelectedOption] = useState<any>();
    const [crmCaseId, setCrmCaseId] = useState('');
    const [isCod, setIsCod] = useState(false);
    const [hasCallCenterRole, setHasCallCenterRole] = useState(false);

    const checkOperatorInfo = JSON.parse(localStorage.getItem('checkOperator') || '{}');
    const selectedShipment = shipment;

    const { data: orderDetails }: any = useSelector((state: any) => state.orderDetails);

    const countryId = useMemo(() => {
        return orderDetails?.countryId != null
            ? Number(orderDetails.countryId)
            : Number(JSON.parse(localStorage.getItem('selectedCountry') || '{}')?.countryId) || TR_COUNTRY_ID;
    }, [orderDetails?.countryId]);
    useEffect(() => {
        OrderQuery.CheckCancelShipmentCallCenterRole()
            .then((res: any) => {
                setHasCallCenterRole(!!res);
            })
            .catch(() => setHasCallCenterRole(false));
    }, []);
    useEffect(() => {
        if (!orderDetails?.orderId) return;
        PaymentCommand.GetPaymentsWithOrderIdList({ orderId: orderDetails.orderId })
            .then((res: any) => {
                const hasCod = res?.payments?.some((p: any) => p.paymentMethodId === COD_PAYMENT_METHOD_ID);
                setIsCod(!!hasCod);
            })
            .catch(() => setIsCod(false));
    }, [orderDetails?.orderId]);

    const isTrCountry = countryId === TR_COUNTRY_ID;
    const showCrmCaseIdField = isCod;
    const showNonTrCodInfo =
        !isTrCountry && isCod && selectedState == ShippingListDeliveredStatus.toString() && hasCallCenterRole;
    enum verificationType {
        OTP = 0,
        Identity = 1,
    }
    const hasCancelStatus = (shipmentStatuses || []).some((s: any) => s.toStatusId === 2);

    useEffect(() => {
        if (!hasCancelStatus) return;
        OrderQuery.GetOrderCancelReason().then((res) => {
            setCancelReasons(res);
        });
    }, [shipmentStatuses]);

    useEffect(() => {
        if (!selectedShipment) {
            setShipmentStatuses([]);
            return;
        }

        const abortController = new AbortController();
        const timeoutId = setTimeout(() => abortController.abort(), 30000);

        setIsLoading(true);
        const req = {
            userRef: checkOperatorInfo.userRef,
            fromStatusId: selectedShipment.statusId,
            isSellerStore: selectedShipment.isSellerStore,
            shipmentId: selectedShipment.shippingListId,
            customerId: orderDetails.customerId,
        };

        OrderQuery.GetOrderShipmentStatusForStatusChange(req, abortController.signal)
            .then((res) => {
                setShipmentStatuses(res.data);
                setShipmentStatusState(res.data);
            })
            .catch(() => {
                setIsError(true);
            })
            .finally(() => {
                clearTimeout(timeoutId);
                setIsLoading(false);
            });

        return () => {
            abortController.abort();
            clearTimeout(timeoutId);
        };
    }, [shipment]);

    const handleSelectChange = (event: any) => {
        event.stopPropagation();
        setSelectedState(event.target.value);
        setTextBoxValue('');
        setSelectedCancelState('');
        setCrmCaseId('');
    };
    const handleCancelSelectChange = (event: any) => {
        event.stopPropagation();
        setTextBoxValue('');
        let orderCancelReasonText: any = cancelReasons.data.find(
            (item: any) => item.descriptionKey == event.target.value
        );
        setSelectedCancelState(orderCancelReasonText?.descriptionKey);
    };

    useEffect(() => {
        if (shipmentStatusState !== null && shipmentStatusState.length == 0) {
            Swal.fire({
                title: t('orderDetail.error'),
                text: t('orderDetail.noStatusToTransition'),
                icon: 'error',
            });
            handleCloseModal();
        }
    }, [shipmentStatusState]);

    const handleSave = (shippingListId: any) => {
        const validationError = validateShipmentStatusSave({
            selectedState,
            selectedShipment,
            isVerified,
            textBoxDesi,
            textBoxPackageCount,
            selectedCancelState,
            textBoxValue,
            showCrmCaseIdField,
            crmCaseId,
            showNonTrCodInfo,
            t,
        });

        if (validationError) {
            Swal.fire({
                title: t('orderDetail.error'),
                text: validationError,
                icon: 'error',
            });
            return;
        }

        const verification_type = getShipmentVerificationType(selectedOption, verificationType);

        setIsLoading(true);
        OrderCommand.UpdateShippingStatus({
            newStatus: selectedState,
            shippingListId: shippingListId,
            description: showCrmCaseIdField && crmCaseId?.trim() ? crmCaseId.trim() : textBoxValue,
            oldStatus: selectedShipment.statusId,
            cancelReason: selectedCancelState,
            desi: textBoxDesi ? Number.parseInt(textBoxDesi) : null,
            packageCount: textBoxPackageCount ? Number.parseInt(textBoxPackageCount) : null,
            isSellerStore: selectedShipment.isSellerStore,
            verificationType: verification_type,
            customerId: orderDetails.customerId,
            cancelReasonId:
                cancelReasons?.data?.find((item: any) => item.descriptionKey == selectedCancelState)?.reasonId ?? null,
            orderId: orderDetails.orderId,
        })
            .then((response: any) => {
                if (response || response?.isSucceed == true) {
                    Swal.fire({
                        title: t('orderDetail.processing'),
                        html: t('orderDetail.statusUpdating'),
                        timer: 6000,
                        timerProgressBar: true,
                        didOpen: () => {
                            Swal.showLoading();
                        },
                        willClose: () => {
                            handleCloseModal();
                            window.location.reload();
                        },
                    }).then((result) => {
                        if (result.dismiss === Swal.DismissReason.timer) {
                            Swal.fire({
                                title: t('general.success'),
                                text: t('orderDetail.statusUpdateSuccess'),
                                icon: 'success',
                            });
                        }
                    });
                } else {
                    Swal.fire({
                        title: t('orderDetail.error'),
                        text: t('orderDetail.statusUpdateError'),
                        icon: 'error',
                    });
                    setIsLoading(false);
                }
            })
            .catch(() => {
                Swal.fire({
                    title: t('orderDetail.error'),
                    text: t('orderDetail.selectStatus'),
                    icon: 'error',
                });
                setIsLoading(false);
            })
            .finally(() => {
                setIsLoading(false);
            });
    };
    if (isLoading) return <div className={UOSSU.loadingState}>{t('orderDetail.loading')}</div>;
    if (isError) return <div className={UOSSU.errorState}>{t('orderDetail.error')}</div>;
    return (
        <Card className={UOSSU.rootCard} variant="elevation" style={{ padding: '0.4rem' }}>
            <div className={UOSSU.headerRow} style={{ display: 'flex', justifyContent: 'space-between' }}>
                <h2 className={UOSSU.title} style={{ marginLeft: '1.6rem', fontSize: '18px' }}>
                    {t('orderDetail.updateShipmentStatus')}
                </h2>
                <Button className={UOSSU.inputButtonClose} onClick={() => handleCloseModal()}>
                    <CloseIcon className={UOSSU.closeIcon} style={{ height: 'auto', color: 'black', width: '25px' }} />
                </Button>
            </div>

            <CardContent className={UOSSU.content}>
                <Divider className={UOSSU.divider} style={{ marginBottom: '1rem' }} />
                <div className={UOSSU.formColumn} style={{ display: 'flex', flexDirection: 'column' }}>
                    <FormControl className={UOSSU.mainFormControl} fullWidth style={{ marginBottom: '1rem', maxWidth: '300px' }}>
                        <Select
                            className={UOSSU.inputSelectNewStatus}
                            value={selectedState}
                            onChange={handleSelectChange}
                            displayEmpty
                            inputProps={{ 'aria-label': 'Without label' }}
                        >
                            <MenuItem value="" disabled>
                                {t('orderDetail.selectStatus')}
                            </MenuItem>
                            {shipmentStatuses &&
                                shipmentStatuses.map((status: any, index: any) => (
                                    <MenuItem key={index} value={status.toStatusId}>
                                        {status.fromStatusDescription}
                                    </MenuItem>
                                ))}
                        </Select>
                        {cancelReasons && selectedState == '2' && (
                            <>
                                <p className={UOSSU.cancelReasonLabel}>{t('orderDetail.shipmentCancelReason')}</p>
                                <Select
                                    className={UOSSU.inputSelectCancelReason}
                                    value={selectedCancelState}
                                    onChange={handleCancelSelectChange}
                                    displayEmpty
                                    inputProps={{ 'aria-label': 'Without label' }}
                                >
                                    <MenuItem value={selectedCancelState} disabled>
                                        {t('orderDetail.selectStatus')}
                                    </MenuItem>
                                    {cancelReasons.data.map(
                                        (status: any, index: any) =>
                                            status.descriptionKey != 'ChooseStatus' && (
                                                <MenuItem key={index} value={status.descriptionKey}>
                                                    {status.descriptionKey}
                                                </MenuItem>
                                            )
                                    )}
                                </Select>
                                {selectedCancelState && (
                                    <TextField
                                        className={UOSSU.inputTextCancelDetail}
                                        fullWidth
                                        style={{
                                            marginBottom: '1rem',
                                            maxWidth: '300px',
                                            marginTop: '1rem',
                                        }}
                                        value={textBoxValue}
                                        onChange={(e) => setTextBoxValue(e.target.value)}
                                        label={t('orderDetail.enterCancelReasonPlaceholder')}
                                        variant="outlined"
                                    />
                                )}
                            </>
                        )}
                        {showNonTrCodInfo && selectedState && (
                            <>
                                <TextField
                                    className={UOSSU.inputTextCrmCaseId}
                                    fullWidth
                                    style={{
                                        marginBottom: '1rem',
                                        maxWidth: '300px',
                                        marginTop: '1rem',
                                    }}
                                    value={crmCaseId}
                                    onChange={(e) => setCrmCaseId(e.target.value)}
                                    label={t('orderDetail.deliveryImageCrmCaseIdPlaceholder')}
                                    placeholder={t('orderDetail.deliveryImageCrmCaseIdPlaceholder')}
                                    variant="outlined"
                                    required
                                    error={!crmCaseId?.trim()}
                                />
                                {showNonTrCodInfo && (
                                    <Typography
                                        className={UOSSU.crmCaseIdHint}
                                        variant="body2"
                                        color="text.secondary"
                                        style={{ marginTop: '-0.5rem', marginBottom: '0.5rem', maxWidth: '300px' }}
                                    >
                                        {t('orderDetail.crmCaseIdInfoNonTrCod')}
                                    </Typography>
                                )}
                            </>
                        )}
                        {selectedState == '19' && selectedShipment.statusId == 5 && selectedShipment.isSellerStore && (
                            <PackageDataDispatcher
                                textBoxDesiState={textBoxDesi}
                                setTextBoxDesi={setTextBoxDesi}
                                textBoxPackageCountState={textBoxPackageCount}
                                setTextBoxPackageCount={setTextBoxPackageCount}
                            />
                        )}

                        {selectedState == '6' && selectedShipment.statusId == 29 && (
                            <OtpOrIdentityVerification
                                selectedShipment={selectedShipment}
                                otpVerificationCode={otpVerificationCode}
                                setOtpVerificationCode={setOtpVerificationCode}
                                isVerified={isVerified}
                                setIsVerified={setIsVerified}
                                handleCloseModal={handleCloseModal}
                                selectedOption={selectedOption}
                                setSelectedOption={setSelectedOption}
                            />
                        )}
                    </FormControl>

                    <SCUIButton
                        className={UOSSU.inputButtonSave}
                        style={{ maxWidth: '300px' }}
                        onClick={() => handleSave(shippingListId)}
                        type={'primary'}
                        sizeType={'fixed'}
                        disabled={isLoading}
                        size={'s'}
                        text={t('orderDetail.save')}
                    ></SCUIButton>
                </div>
            </CardContent>
        </Card>
    );
}

export default UserOrderShipmentStatusUpdate;
